import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { productsApi, type ProductFilters } from "@/lib/admin-api/products";
import { useAdminRealtime } from "@/lib/admin-api/realtime";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const PRODUCTS_KEY = ["admin", "products"] as const;

function productsQueryKey(filters: ProductFilters) {
  return [...PRODUCTS_KEY, filters] as const;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch paginated product list with filters */
export function useProductsList(filters: ProductFilters = {}) {
  // Subscribe to realtime changes so the table auto-refreshes
  useAdminRealtime("products", [...PRODUCTS_KEY]);

  return useQuery({
    queryKey: productsQueryKey(filters),
    queryFn: () => productsApi.list(filters),
    staleTime: 30_000,
  });
}

/** Fetch a single product by ID */
export function useProduct(id: string | null) {
  return useQuery({
    queryKey: [...PRODUCTS_KEY, id],
    queryFn: () => productsApi.get(id!),
    enabled: !!id,
  });
}

/** Create a new product */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) => productsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PRODUCTS_KEY] });
    },
  });
}

/** Update an existing product */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      productsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PRODUCTS_KEY] });
    },
  });
}

/** Soft-delete (deactivate) a product */
export function useDeactivateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PRODUCTS_KEY] });
    },
  });
}

/** Bulk CSV import */
export function useImportProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rows: readonly Record<string, unknown>[]) =>
      productsApi.importCSV(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...PRODUCTS_KEY] });
    },
  });
}
